import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { AccordionDetails, AccordionSummary, Box, Button, Flex, Typography, Accordion } from "flux";
import useAdAssets from "../../hooks/use-ad-assets";
import { FacebookPlacementSettings } from "@adora/shared/features/ad-publishing/types";
import { PlacementDisplayNames } from "@adora/shared/features/ad-publishing/helpers";
import useAdAssetGroup from "../../hooks/use-ad-asset-group";
import AdAssetDetailsTable from "./ad-asset-details-table";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { getLocaleTimeString } from "@adora/shared/utils/date";
import { AdAssetGroup } from "../../types/ad-asset-group";
import UpdateAdAssetGroupDialog from "../update-ad-asset-group-dialog";
import BulkUpdateAdAssetDialog from "../update-bulk-ad-asset-dialog";
import { ProcessedAdAsset } from "./types";
import AdAssetImagesTable from "./ad-asset-images-table";
import BulkReplaceDialog from "./bulk-replace-dialog";
import BatchesTable from "./batches-table";
import AdImagesTable from "./ad-images-table";
import OriginalAdImagesTable from "./original-ad-images-table";
import Page from "@adora/shared/components/layout/page";
import useBrandName from "../../hooks/use-brand-name";
import { createOrUpdateAdAssetsFromAdBuilder } from "../../features/ad-publishing/api";
import { useToast } from "@adora/shared/hooks/use-toast";
import { useBrandAdAccounts, BrandAdAccount } from "../../hooks/use-brand-ad-accounts";
import { isInternalAppStaging } from "@adora/shared/utils/environment";

type AdCopyField = keyof Pick<
  ProcessedAdAsset,
  | "google_descriptions"
  | "google_long_headline"
  | "google_headlines"
  | "google_call_to_action_type"
  | "facebook_bodies"
  | "facebook_headlines"
  | "facebook_description"
  | "facebook_call_to_action_type"
  | "facebook_placement_settings"
  | "msan_headlines"
  | "msan_long_headline"
  | "msan_descriptions"
  | "msan_call_to_action_type"
>;

const adCopyFields: AdCopyField[] = [
  "google_descriptions",
  "google_long_headline",
  "google_headlines",
  "google_call_to_action_type",
  "facebook_headlines",
  "facebook_description",
  "facebook_bodies",
  "facebook_call_to_action_type",
  "msan_descriptions",
  "msan_long_headline",
  "msan_headlines",
  "msan_call_to_action_type"
];

type AdSettingsField = keyof Pick<
  ProcessedAdAsset,
  "name" | "google_destination_url" | "facebook_destination_url" | "msan_destination_url" | "comments"
>;

const adSettingsFields: AdSettingsField[] = [
  "name",
  "google_destination_url",
  "facebook_destination_url",
  "msan_destination_url",
  "comments"
];

type InternalField = keyof Pick<
  ProcessedAdAsset,
  | "platforms"
  | "google_ad_group_id"
  | "meta_ad_set_id"
  | "msan_ad_group_id"
  | "created_at"
  | "ad_asset_group_id"
  | "brand_id"
  | "updated_at"
  | "ad_asset_group_comments"
>;

const internalFields: InternalField[] = [
  "created_at",
  "brand_id",
  "ad_asset_group_id",
  "updated_at",
  "ad_asset_group_comments"
];

type GroupDetailsField = keyof Pick<
  AdAssetGroup,
  | "name"
  | "description"
  | "platforms"
  | "google_ad_group_id"
  | "meta_ad_set_id"
  | "msan_ad_group_id"
  | "tracking_id_start"
  | "google_campaign_name"
  | "google_ad_group_name"
  | "msan_campaign_name"
  | "msan_ad_group_name"
  | "meta_campaign_name"
  | "meta_ad_set_name"
>;

const groupDetailsFields: GroupDetailsField[] = [
  "name",
  "description",
  "platforms",
  "tracking_id_start",
  "google_ad_group_id",
  "meta_ad_set_id",
  "msan_ad_group_id",
  "google_campaign_name",
  "google_ad_group_name",
  "msan_campaign_name",
  "msan_ad_group_name",
  "meta_campaign_name",
  "meta_ad_set_name"
];

type PlacementField = keyof Pick<ProcessedAdAsset, "facebook_placement_settings">;

const placementFields: PlacementField[] = ["facebook_placement_settings"];

function getGroupDetailsData(adAssetGroup: AdAssetGroup, adAccounts?: BrandAdAccount[]) {
  return Object.fromEntries(
    groupDetailsFields.map((field) => {
      const value = adAssetGroup[field] || "";

      // Special handling for platforms field
      if (field === "platforms" && Array.isArray(value) && adAccounts) {
        const formattedPlatforms = value.map((platform) => {
          if (platform === "meta") {
            // Get the meta account ID from the ad asset group
            const metaAccountId =
              (adAssetGroup as unknown as Record<string, string>)["meta_ad_account_id"] || adAssetGroup.meta_ad_set_id;

            if (metaAccountId) {
              // Find the matching account
              const metaAccount = adAccounts.find(
                (account) => account.platform === "meta" && account.account_id === metaAccountId
              );
              if (metaAccount) {
                return `meta: (${metaAccount.account_id})`;
              }
            }
            return platform;
          }
          return platform;
        });
        return [field, formattedPlatforms];
      }

      if (field.endsWith("_name")) {
        const urlField = field.replace("_name", "_url") as keyof AdAssetGroup;
        const url = adAssetGroup[urlField];
        return [field, url ? { type: "url", value: url, displayText: value } : value];
      }
      return [field, value];
    })
  );
}
function getData<T extends keyof ProcessedAdAsset>(asset: ProcessedAdAsset, fieldsToExtract: T[]) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.fromEntries(fieldsToExtract.map((field) => [field, asset[field]]).filter(([_, v]) => v)) as Record<
    T,
    string | string[] | number
  >;
}

function getAdCopyData(asset: ProcessedAdAsset) {
  return getData<AdCopyField>(asset, adCopyFields);
}

function getAdSettingsData(asset: ProcessedAdAsset) {
  return getData<AdSettingsField>(asset, adSettingsFields);
}

function getInternalData(asset: ProcessedAdAsset) {
  return getData<InternalField>(asset, internalFields);
}

function getPlacementData(asset: ProcessedAdAsset) {
  return getData<PlacementField>(asset, placementFields);
}

const AdAssetsPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const brandName = useBrandName(brandId);
  const { adAssetGroupId } = useParams<{ adAssetGroupId: string }>();
  const {
    adAssets,
    isLoading: assetsLoading,
    error,
    invalidateAdAssetsCache
  } = useAdAssets(Number(adAssetGroupId), Number(brandId));
  const { adAssetGroup: currentAdAssetGroup, invalidateAdAssetGroupCache } = useAdAssetGroup(
    Number(adAssetGroupId),
    Number(brandId)
  );
  const { data: adAccounts } = useBrandAdAccounts(Number(brandId));
  const { openDialog } = useDialog();
  const { openToast } = useToast();

  const [fusingImages, setFusingImages] = useState<boolean>(false);

  const formatArrayField = (arr: string[] | null | undefined): string => {
    if (!arr || arr.length === 0) return "";
    return arr.join(", ");
  };

  const formatPlacementSettings = (settings?: FacebookPlacementSettings): string => {
    if (!settings) return "";

    const allPlacements = [
      ...(settings.facebook_positions || []).map((pos) => PlacementDisplayNames[`facebook.${pos}`]),
      ...(settings.instagram_positions || []).map((pos) => PlacementDisplayNames[`instagram.${pos}`]),
      ...(settings.messenger_positions || []).map((pos) => PlacementDisplayNames[`messenger.${pos}`]),
      ...(settings.audience_network_positions || []).map((pos) => PlacementDisplayNames[`audience_network.${pos}`])
    ];

    return allPlacements.join(", ");
  };

  const AdMetadataTable: React.FC<{ adAsset: ProcessedAdAsset; adAssetGroup: AdAssetGroup }> = ({
    adAsset,
    adAssetGroup
  }) => {
    const adCopyData = getAdCopyData(adAsset);
    const additionalData = getAdSettingsData(adAsset);
    const groupDetailsData = getGroupDetailsData(adAssetGroup, adAccounts);
    const internalData = getInternalData(adAsset);
    const placementData = getPlacementData(adAsset);
    return (
      <Box mb={4}>
        <Typography variant="h2" mb={2}>
          Ad Asset Group Info
        </Typography>
        <Typography variant="h6" mb={2}>
          Internal
        </Typography>
        <AdAssetDetailsTable<InternalField> tableData={internalData} />
        <Typography variant="h6" mb={2}>
          Group Details
        </Typography>
        <AdAssetDetailsTable<GroupDetailsField> tableData={groupDetailsData} />
        <Typography variant="h6" mb={2}>
          Ad Copy
        </Typography>
        <AdAssetDetailsTable<AdCopyField> tableData={adCopyData} />
        <Typography variant="h6" mb={2}>
          Ad Settings
        </Typography>
        <AdAssetDetailsTable<AdSettingsField> tableData={additionalData} />
        <Typography variant="h6" mb={2}>
          Ad Placements
        </Typography>
        <AdAssetDetailsTable<PlacementField> tableData={placementData} />
      </Box>
    );
  };

  const processedAdAssets = adAssets.map((asset) => {
    // Find images by type
    const squareImage = asset.images
      .filter((img) => img.type === "square")
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    const landscapeImage = asset.images
      .filter((img) => img.type === "landscape")
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    const portraitImage = asset.images
      .filter((img) => img.type === "portrait")
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    const feedImage = asset.images
      .filter((img) => img.type === "feed")
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    return {
      ...asset,
      name: asset.name,
      created_at: getLocaleTimeString(asset.created_at),
      updated_at: getLocaleTimeString(asset.updated_at),
      google_headlines: formatArrayField(asset.google_headlines),
      google_descriptions: formatArrayField(asset.google_descriptions),
      google_long_headline: asset.google_long_headline,
      google_call_to_action_type: asset.google_call_to_action_type,
      facebook_headlines: formatArrayField(asset.facebook_headlines),
      facebook_description: asset.facebook_description,
      facebook_bodies: formatArrayField(asset.facebook_bodies),
      facebook_call_to_action_type: asset.facebook_call_to_action_type,
      facebook_placement_settings: formatPlacementSettings(asset.facebook_placement_settings),
      msan_headlines: formatArrayField(asset.msan_headlines),
      msan_descriptions: formatArrayField(asset.msan_descriptions),
      msan_long_headline: asset.msan_long_headline,
      msan_call_to_action_type: asset.msan_call_to_action_type,
      square_image_url: squareImage?.url,
      landscape_image_url: landscapeImage?.url,
      portrait_image_url: portraitImage?.url,
      feed_image_url: feedImage?.url,
      comments: (asset?.comments || []).map(({ content }) => content || ""),
      ad_asset_group_comments: (currentAdAssetGroup?.comments || []).map(({ content }) => content || ""),
      square_image: squareImage,
      landscape_image: landscapeImage,
      portrait_image: portraitImage,
      feed_image: feedImage,
      is_deleted: asset.is_deleted,
      platforms: currentAdAssetGroup?.platforms?.join(", ") || "",
      google_ad_group_id: currentAdAssetGroup?.google_ad_group_id,
      meta_ad_set_id: currentAdAssetGroup?.meta_ad_set_id,
      msan_ad_group_id: currentAdAssetGroup?.msan_ad_group_id,
      video_id: asset.video_id,
      status: asset.status
    };
  });

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }
  const assemblingStatusQuery = ["assembling", "draft"].includes(currentAdAssetGroup?.status ?? "")
    ? `/assembling`
    : "";
  const baseUrl = isInternalAppStaging() ? "https://staging.adora-ai.com" : "https://app.adora-ai.com";
  const deepLink = `${baseUrl}/ad-builder/${adAssetGroupId}${assemblingStatusQuery}?aspectRatio=${currentAdAssetGroup?.aspect_ratios?.[0] ?? "square"}`;

  const handleBulkEditAdAssets = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const result = await openDialog(BulkUpdateAdAssetDialog, {
      brandId: Number(brandId),
      adAssetGroupId
    });
    if (result) {
      invalidateAdAssetsCache();
    }
  };

  const handleEditAdAssetGroup = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const result = await openDialog(UpdateAdAssetGroupDialog, {
      brandId: Number(brandId),
      adAssetGroupId
    });
    if (result) {
      invalidateAdAssetGroupCache();
    }
  };

  const handleGenerateAdAssets = async (adAssetGroupId: number) => {
    if (!brandId) return;
    setFusingImages(true);
    openToast("Generating ad assets...");
    const result = await createOrUpdateAdAssetsFromAdBuilder(Number(brandId), adAssetGroupId);
    setFusingImages(false);
    if (result && result.success) {
      openToast("Ad assets generated successfully");
      invalidateAdAssetsCache();
    } else {
      openToast("Failed to generate ad assets");
    }
  };

  return (
    <Page
      backLinkText={brandName}
      title="Ad Assets"
      titleBarActions={
        currentAdAssetGroup && (
          <Box>
            <Button
              variant="contained"
              disabled={fusingImages}
              loading={fusingImages}
              onClick={() => handleGenerateAdAssets(currentAdAssetGroup.id)}
              dataDogIdentifier="ad assets page fuse ad builder images"
            >
              Fuse Ad Builder Images
            </Button>
            <Button
              variant="contained"
              sx={{ marginLeft: "16px" }}
              onClick={() => handleEditAdAssetGroup(currentAdAssetGroup.id)}
              dataDogIdentifier="ad assets page edit group details"
            >
              Edit Group Details
            </Button>
            <Button
              variant="contained"
              sx={{ marginLeft: "16px" }}
              onClick={() => handleBulkEditAdAssets(currentAdAssetGroup.id)}
              dataDogIdentifier="ad assets page edit ad details (bulk)"
            >
              Edit Ad Details (bulk)
            </Button>
          </Box>
        )
      }
    >
      {currentAdAssetGroup && processedAdAssets.length > 0 && (
        <AdMetadataTable adAsset={processedAdAssets[0]} adAssetGroup={currentAdAssetGroup} />
      )}

      <Box>
        <Typography variant="h2" mb={2}>
          Ad Previews
        </Typography>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Button
            variant="contained"
            onClick={async () => {
              if (brandId && adAssetGroupId) {
                await openDialog(BulkReplaceDialog, {
                  brandId,
                  adAssetGroupId,
                  refetchAdAssets: invalidateAdAssetsCache
                });
              }
            }}
            dataDogIdentifier="bulk replace ad assets"
          >
            Bulk replace
          </Button>
          <Typography variant="body1">Total Ad Assets: {adAssets.length}</Typography>
        </Flex>
        {processedAdAssets.length > 0 && (
          <AdAssetImagesTable
            processedAdAssets={processedAdAssets}
            invalidateAdAssetsCache={invalidateAdAssetsCache}
            loading={assetsLoading}
            deepLink={deepLink}
          />
        )}
        <OriginalAdImagesTable adAssetGroupId={Number(adAssetGroupId)} />
        <Box pt={2}>
          <Accordion>
            <AccordionSummary>
              <Typography>Original Ad Images (Old)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AdImagesTable adAssetGroupId={Number(adAssetGroupId)} />
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
      {brandId && adAssetGroupId && (
        <BatchesTable
          platforms={currentAdAssetGroup?.platforms || []}
          brandId={Number(brandId)}
          adAssetGroupId={Number(adAssetGroupId)}
        />
      )}
    </Page>
  );
};

export default AdAssetsPage;
